import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'ha/i18n';
import { Actions as NavBarActions } from 'ha/modules/NavBar/actions';
import { normalizeApiV2Errors } from 'ha/helpers/forms/normalizeApiV2Errors';
import dayjs from 'dayjs';
import { setInStorage } from 'ha/utils/storage';
import { getCurrentLanguageCode } from 'ha/modules/LanguageSwitcher/selectors';
import { Payload as LoginPayload } from 'ha/services/apiV2/login';
import { Payload as RegisterPayload } from 'ha/services/apiV2/register';
import { Dispatch } from 'ha/myredux/types';
import { loginRequest, registerRequest } from './actions';
import { getIsLoading } from './selectors';

export type OnLogin = (
  nextUrl?: string,
) => (fields: LoginPayload) => Promise<void>;

export type OnRegister = (
  nextUrl?: string,
) => (fields: Partial<RegisterPayload>) => Promise<void>;

interface Props {
  children(authentication: {
    onLogin: OnLogin;
    onRegister: OnRegister;
    isLoading: boolean;
  }): React.ReactElement;
}

export const AuthLogic: React.FC<Props> = ({ children }) => {
  const { T } = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const isLoading = useSelector(getIsLoading);
  const notificationLang = useSelector(getCurrentLanguageCode);

  const onLogin = React.useCallback(
    nextUrl => (fields: LoginPayload) =>
      dispatch(loginRequest(fields, nextUrl))
        .then(() => {
          dispatch(NavBarActions.hideModal());
        })
        .catch(err => Promise.reject(normalizeApiV2Errors(T, err))),
    [],
  );

  const onRegister = React.useCallback(
    nextUrl => (fields: RegisterPayload) =>
      dispatch(registerRequest({ ...fields, notificationLang }, nextUrl))
        .then(() => {
          setInStorage('verifyEmailDialogLastShown', dayjs().valueOf());
          dispatch(NavBarActions.hideModal());
        })
        .catch(err => Promise.reject(normalizeApiV2Errors(T, err))),
    [],
  );

  return children({ onLogin, onRegister, isLoading });
};
